import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable, ReplaySubject, take } from 'rxjs';

export interface Language {
    title: string;
    iconClass: string;
    code: 'cs' | 'en' | string;
}

export interface LocalStorageSettings {
    lang?: string;
    tourCompleted?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class SettingsService {
    private _settingsServiceKey = 'settings';

    /**
     * The list of languages available in the application.
     *
     * @type {Language[]}
     */
    private _languages: Language[] = [
        {
            title: 'Automaticky',
            iconClass: '',
            code: 'by-system',
        },
        {
            title: 'Čeština',
            iconClass: 'flag-icon flag-icon-cz',
            code: 'cs',
        },
        {
            title: 'English',
            iconClass: 'flag-icon flag-icon-gb',
            code: 'en',
        },
        {
            title: 'Slovenština',
            iconClass: 'flag-icon flag-icon-sk',
            code: 'sk',
        },
        {
            title: 'Polski',
            iconClass: 'flag-icon flag-icon-pl',
            code: 'pl',
        },
    ];

    /**
     * Retrieves all available languages.
     *
     * @return {Language[]} The array containing all available languages.
     */
    get languages(): Language[] {
        return this._languages;
    }

    /**
     * Represents a ReplaySubject that emits and replays the latest value of LocalStorageSettings.
     *
     * @type {ReplaySubject<LocalStorageSettings>}
     */
    private _settingsSubject: ReplaySubject<LocalStorageSettings> = new ReplaySubject<LocalStorageSettings>(1);

    /**
     * Get all settings as observable
     *
     * @return {Observable<LocalStorageSettings>} Observable that emits the current settings
     */
    get settings$(): Observable<LocalStorageSettings> {
        return this._settingsSubject;
    }

    /**
     * Get actual settings
     *
     * @return {Promise<LocalStorageSettings>} A promise that resolves to the actual settings
     */
    get settings(): Promise<LocalStorageSettings> {
        return firstValueFrom(this.settings$.pipe(take(1)));
    }

    private langSubject = new ReplaySubject<Language>(1);

    get currentLang$(): Observable<Language> {
        return this.langSubject.asObservable();
    }

    constructor(private translate: TranslateService) {}

    /**
     * Init settings.
     *
     * Initializes the language and settings from local storage.
     *
     * @return {Promise<void>} A promise that resolves once the settings are initialized.
     */
    async init(): Promise<void> {
        // set default language
        this.translate.setDefaultLang('cs');

        // get settings from storage
        const ret = localStorage.getItem(this._settingsServiceKey);
        const settings = JSON.parse(ret ?? '{}');
        this._settingsSubject.next(settings);

        // if stored lang in DB, set it
        this.changeLanguage(settings?.lang);
    }

    /**
     * Change language of APP and store it in settings.
     *
     * @param {string} lang - Language code without culture: en, cs.
     * @returns {Promise<void>} - A Promise that resolves when the language is changed and the settings are updated.
     */
    async useLanguage(lang: string): Promise<void> {
        this.changeLanguage(lang);

        // save to localstorage
        await this.updateSettings({
            lang: lang,
        });
    }

    async setTourCompleted(): Promise<void> {
        await this.updateSettings({
            tourCompleted: true,
        });
    }

    /**
     * Update settings
     *
     * @param {LocalStorageSettings} settings - The new settings to update. It should be an object of type LocalStorageSettings.
     * @return {Promise<void>} - Returns a promise that resolves when the settings are updated.
     */
    protected async updateSettings(settings: LocalStorageSettings): Promise<void> {
        const loadedSettings = await this.settings;

        const newSettings = {
            ...loadedSettings, // loaded settings
            ...settings, // new settings
        };

        this._settingsSubject.next(newSettings);

        // save to local DB
        localStorage.setItem(this._settingsServiceKey, JSON.stringify(newSettings));
    }

    /**
     * Change language in local environment.
     *
     * @param {string} lang - Language without culture: en, cs.
     * @returns {void}
     */
    protected changeLanguage(lang: string): void {
        if (lang !== undefined && lang !== 'by-system') {
            this.translate.setDefaultLang(lang);
            this.translate.use(lang);
        } else {
            const browserLang = (navigator.language || navigator.languages[0]).split('-')[0];
            if (this.languages.map((l) => l.code).includes(browserLang)) {
                this.translate.setDefaultLang(browserLang);
                this.translate.use(browserLang);
            } else {
                this.translate.setDefaultLang('cs');
                this.translate.use('cs');
            }
        }
        this.langSubject.next(this.languages.find((l) => l.code === this.translate.currentLang)!);
    }
}
